import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MediaItem, MediaCategory } from '../../../../typings/media.interface';

export interface EditMediaDialogData {
  media: MediaItem;
  availableLabels: string[];
  availableCategories: MediaCategory[];
}

export interface EditMediaDialogResult {
  title: string;
  description: string;
  caption: string;
  category?: MediaCategory;
  labels: string[];
}

@Component({
  selector: 'app-edit-media-dialog',
  templateUrl: './edit-media-dialog.component.html',
  styleUrls: ['./edit-media-dialog.component.scss']
})
export class EditMediaDialogComponent implements OnInit {
  editForm: FormGroup;
  
  // Chip configuration
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  
  // Available labels for autocomplete
  filteredLabels: string[] = [];
  
  // Current input values
  labelInput = '';
  
  // Categories
  categories: MediaCategory[] = [
    'Nature', 'Tech', 'People', 'Animals', 'Food', 'Travel', 'Art', 'Sports', 'Other'
  ];

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<EditMediaDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditMediaDialogData
  ) {
    this.editForm = this.fb.group({
      title: [data.media.title || '', Validators.required],
      description: [data.media.description || ''],
      caption: [data.media.caption || ''],
      category: [data.media.category || ''],
      labels: [data.media.labels || []]
    });
  }

  ngOnInit(): void {
    this.filteredLabels = this.data.availableLabels;
  }

  // ---------------------------------------------------------------------------
  // Label chips
  // ---------------------------------------------------------------------------
  
  addLabel(event: any): void {
    const value = (event.value || '').trim();
    if (value) {
      const labels = this.editForm.get('labels')?.value || [];
      if (!labels.includes(value)) {
        labels.push(value);
        this.editForm.patchValue({ labels });
      }
    }
    this.labelInput = '';
    event.chipInput?.clear();
  }

  removeLabel(label: string): void {
    const labels = this.editForm.get('labels')?.value || [];
    const index = labels.indexOf(label);
    if (index >= 0) {
      labels.splice(index, 1);
      this.editForm.patchValue({ labels });
    }
  }

  // ---------------------------------------------------------------------------
  // Filtering
  // ---------------------------------------------------------------------------
  
  filterLabels(): void {
    const filterValue = this.labelInput.toLowerCase();
    this.filteredLabels = this.data.availableLabels.filter(label =>
      label.toLowerCase().includes(filterValue)
    );
  }

  // ---------------------------------------------------------------------------
  // Dialog actions
  // ---------------------------------------------------------------------------
  
  onSave(): void {
    if (this.editForm.valid) {
      const result: EditMediaDialogResult = {
        title: this.editForm.value.title,
        description: this.editForm.value.description,
        caption: this.editForm.value.caption,
        category: this.editForm.value.category || undefined,
        labels: this.editForm.value.labels
      };
      this.dialogRef.close(result);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}